/**
 * Global word dictionary management UI (body only — no page/dialog chrome).
 * Used both by the standalone DictionaryPage and the combined ManagementModal.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { IconTooltip } from '../ui/tooltip';
import { dictionaryApi } from '../../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { DeleteDictionaryEntryDialog } from '../DeleteDictionaryEntryDialog';
import { useDictionary } from '../../hooks/useDictionary';
import type { WordDictionaryEntry } from '../../types';

type ActiveTab = 'always_allow' | 'always_redact';

export function DictionaryManager() {
  const { entries, isLoading, error, addEntry, updateEntry, deleteEntry } = useDictionary('global');

  const [activeTab, setActiveTab] = useState<ActiveTab>('always_redact');

  // Add form state
  const [newWord, setNewWord] = useState('');
  const [newMatchType, setNewMatchType] = useState<'exact' | 'contains'>('exact');
  const [newReason, setNewReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingWord, setEditingWord] = useState('');
  const [editingMatchType, setEditingMatchType] = useState<'exact' | 'contains'>('exact');
  const [editingReason, setEditingReason] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<WordDictionaryEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Occurrence counts (blast radius) per entry id, across all of the tenant's jobs.
  // null while loading / on error — the column then renders a neutral placeholder.
  const [occurrenceCounts, setOccurrenceCounts] = useState<Record<string, number> | null>(null);

  const fetchCounts = useCallback(async () => {
    try {
      const { counts } = await dictionaryApi.getOccurrenceCounts({ scope_type: 'global' });
      setOccurrenceCounts(counts);
    } catch (err) {
      // Non-critical enrichment — keep the dictionary usable if counts fail.
      setOccurrenceCounts(null);
      console.error('Failed to fetch dictionary occurrence counts:', err);
    }
  }, []);

  // Refetch when the entry set changes (add/edit/delete triggers a backend rescan
  // that can move the counts), so the displayed blast radius stays roughly current.
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, entries]);

  const filteredEntries = entries.filter((e) => e.action === activeTab);

  const handleAdd = async () => {
    if (!newWord.trim()) return;
    setIsSubmitting(true);
    const result = await addEntry({
      word: newWord.trim(),
      action: activeTab,
      matchType: newMatchType,
      reason: activeTab === 'always_redact' ? newReason.trim() || undefined : undefined,
    });
    setIsSubmitting(false);
    if (result) {
      setNewWord('');
      setNewReason('');
      setNewMatchType('exact');
    }
  };

  const handleStartEdit = (entry: WordDictionaryEntry) => {
    setEditingId(entry.id);
    setEditingWord(entry.word);
    setEditingMatchType(entry.match_type);
    setEditingReason(entry.reason || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingWord('');
    setEditingReason('');
    setEditingMatchType('exact');
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingWord.trim()) return;
    setIsSubmitting(true);
    const result = await updateEntry(editingId, {
      word: editingWord.trim(),
      matchType: editingMatchType,
      reason: activeTab === 'always_redact' ? editingReason.trim() || undefined : undefined,
    });
    setIsSubmitting(false);
    if (result) {
      handleCancelEdit();
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    setIsDeleting(true);
    await deleteEntry(target.id);
    setIsDeleting(false);
  };

  const renderOccurrenceCount = (entry: WordDictionaryEntry) => {
    if (occurrenceCounts === null) {
      return <span className="text-muted-foreground/50">—</span>;
    }
    return <span className="tabular-nums">{(occurrenceCounts[entry.id] ?? 0).toLocaleString()}</span>;
  };

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Words defined here apply across all cases (tenant-wide).
      </p>

      {/* Always Redact / Allow List tabs (Darwin "Tabs Secondary" — segmented, no shadow) */}
      <div className="flex w-fit items-center overflow-hidden rounded-[4px] border border-border mb-6">
        <button
          onClick={() => setActiveTab('always_redact')}
          className={`inline-flex h-8 items-center justify-center px-[13px] text-xs leading-none transition-colors ${
            activeTab === 'always_redact'
              ? 'bg-[#31343a] font-bold text-white'
              : 'bg-white font-normal text-[#818897] hover:bg-[#e1e7f1] hover:text-[#31343a]'
          }`}
        >
          Always Redact
        </button>
        <button
          onClick={() => setActiveTab('always_allow')}
          className={`inline-flex h-8 items-center justify-center px-[13px] text-xs leading-none transition-colors ${
            activeTab === 'always_allow'
              ? 'bg-[#31343a] font-bold text-white'
              : 'bg-white font-normal text-[#818897] hover:bg-[#e1e7f1] hover:text-[#31343a]'
          }`}
        >
          Allow List
        </button>
      </div>

      {/* Add entry form */}
      <div className="bg-white border border-border rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium mb-3">
          Add to {activeTab === 'always_redact' ? 'Always Redact' : 'Allow List'}
        </h3>
        <div className="flex gap-2 items-start flex-wrap">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="Word or phrase"
            className="h-9 border border-border rounded-md px-3 py-2 text-sm flex-1 min-w-[180px] focus:outline-none focus:border-primary"
          />
          <Select
            value={newMatchType}
            onValueChange={(v) => setNewMatchType(v as 'exact' | 'contains')}
          >
            <SelectTrigger className="w-[150px] bg-white border-border focus-visible:border-primary focus-visible:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exact">Exact match</SelectItem>
              <SelectItem value="contains">Contains</SelectItem>
            </SelectContent>
          </Select>
          {activeTab === 'always_redact' && (
            <input
              type="text"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="Reason (optional)"
              className="h-9 border border-border rounded-md px-3 py-2 text-sm flex-1 min-w-[180px] focus:outline-none focus:border-primary"
            />
          )}
          <Button
            onClick={handleAdd}
            disabled={!newWord.trim() || isSubmitting}
            className="shrink-0"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </div>
      </div>

      {/* Entries list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <p className="text-destructive text-sm">{error}</p>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No entries yet. Add a word or phrase above.
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Word / Phrase</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Match</th>
                {activeTab === 'always_redact' && (
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reason</th>
                )}
                <th
                  className="text-right px-4 py-3 font-medium text-muted-foreground w-32 whitespace-nowrap"
                  title="How many times this word currently appears in detected text across all your documents"
                >
                  Occurrences
                </th>
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-muted/20">
                  {editingId === entry.id ? (
                    <>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={editingWord}
                          onChange={(e) => setEditingWord(e.target.value)}
                          className="h-8 border border-border rounded-md px-2 py-1 text-sm w-full focus:outline-none focus:border-primary"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={editingMatchType}
                          onValueChange={(v) => setEditingMatchType(v as 'exact' | 'contains')}
                        >
                          <SelectTrigger size="sm" className="w-[130px] px-2 bg-white border-border focus-visible:border-primary focus-visible:ring-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exact">Exact</SelectItem>
                            <SelectItem value="contains">Contains</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      {activeTab === 'always_redact' && (
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editingReason}
                            onChange={(e) => setEditingReason(e.target.value)}
                            placeholder="Reason"
                            className="h-8 border border-border rounded-md px-2 py-1 text-sm w-full focus:outline-none focus:border-primary"
                          />
                        </td>
                      )}
                      <td className="px-4 py-2 text-right text-muted-foreground">
                        {renderOccurrenceCount(entry)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <IconTooltip label="Save changes">
                            <button
                              onClick={handleSaveEdit}
                              disabled={isSubmitting}
                              className="p-1 hover:bg-green-100 text-green-600 rounded"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          </IconTooltip>
                          <IconTooltip label="Cancel editing">
                            <button
                              onClick={handleCancelEdit}
                              className="p-1 hover:bg-gray-100 text-gray-500 rounded"
                            >
                              ✕
                            </button>
                          </IconTooltip>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 font-mono">{entry.word}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {entry.match_type === 'exact' ? 'Exact' : 'Contains'}
                      </td>
                      {activeTab === 'always_redact' && (
                        <td className="px-4 py-3 text-muted-foreground">{entry.reason || '—'}</td>
                      )}
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {renderOccurrenceCount(entry)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <IconTooltip label="Edit entry">
                            <button
                              onClick={() => handleStartEdit(entry)}
                              className="p-1 hover:bg-gray-100 text-gray-500 rounded"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </IconTooltip>
                          <IconTooltip label="Delete entry">
                            <button
                              onClick={() => setDeleteTarget(entry)}
                              disabled={isDeleting}
                              className="p-1 hover:bg-red-100 text-red-500 rounded disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </IconTooltip>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DeleteDictionaryEntryDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTarget(null);
        }}
        word={deleteTarget?.word ?? ''}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}
